import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * AddServiceModal - Reusable modal for adding or editing services
 * 
 * @param {Boolean} show - Whether to show the modal
 * @param {Function} onClose - Handler to close the modal
 * @param {Function} onConfirm - Handler when confirming (receives serviceData object)
 * @param {Array} serviceCatalog - Array of available services
 * @param {Object} currentPayment - Current payment object to check for existing services
 * @param {Function} formatCurrency - Function to format currency
 * @param {Boolean} isEditing - Whether in edit mode
 * @param {Object} editingService - Service being edited (if any)
 */
const AddServiceModal = ({
  show,
  onClose,
  onConfirm,
  serviceCatalog = [],
  currentPayment = null,
  formatCurrency,
  isEditing = false,
  editingService = null
}) => {
  const [selectedService, setSelectedService] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [customTotal, setCustomTotal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when editing or opening modal
  useEffect(() => {
    if (show) {
      if (isEditing && editingService) {
        // Find the service in catalog
        const catalogService = serviceCatalog.find(s => s.id === editingService.serviceId);
        setSelectedService(catalogService || {
          id: editingService.serviceId,
          name: editingService.name
        });
        setQuantity(editingService.quantity);
        setCustomTotal(editingService.total.toString());
      } else {
        // Reset for new service
        setSelectedService(null);
        setQuantity(1);
        setCustomTotal('');
      }
    }
  }, [show, isEditing, editingService, serviceCatalog]);

  const handleServiceChange = (serviceId) => {
    const service = serviceCatalog.find(s => s.id === parseInt(serviceId));
    setSelectedService(service);
    if (service) {
      // Set default total based on service price
      const price = service.defaultTotal || service.price || 0;
      setCustomTotal((price * quantity).toString());
    } else {
      setCustomTotal('');
    }
  };

  const handleQuantityChange = (newQuantity) => {
    const qty = parseInt(newQuantity) || 1;
    setQuantity(qty);
    
    // Update total if we have a selected service
    if (selectedService) {
      const price = selectedService.defaultTotal || selectedService.price || 0;
      setCustomTotal((price * qty).toString());
    }
  };

  const handleConfirm = async () => {
    if (isSubmitting || !selectedService || quantity <= 0 || !customTotal || parseFloat(customTotal) <= 0) {
      return;
    }

    const serviceData = {
      service: selectedService,
      quantity: quantity,
      total: parseFloat(customTotal),
      editingService: isEditing ? editingService : null
    };

    setIsSubmitting(true);
    try {
      await onConfirm(serviceData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedService(null);
    setQuantity(1);
    setCustomTotal('');
    onClose();
  };

  if (!show) return null;

  // Filter out services already added (except when editing)
  const availableServices = serviceCatalog.filter(service => {
    if (!currentPayment || !currentPayment.services) return true;
    
    // If editing, allow the current service
    if (isEditing && editingService && service.id === editingService.serviceId) {
      return true;
    }
    
    // Filter out already added services
    return !currentPayment.services.some(s => s.serviceId === service.id);
  });

  return (
    <div className="pmt-modal-backdrop">
      <div className="pmt-modal">
        <div className="pmt-modal-header">
          <h2 className="pmt-modal-title">
            {isEditing ? 'Modifier le Service' : 'Ajouter un Service'}
          </h2>
          <button onClick={handleClose} className="pmt-modal-close">
            <X size={24} />
          </button>
        </div>

        <div className="pmt-modal-body">
          {!isEditing && (
            <div className="pmt-form-group">
              <label className="pmt-label">Service</label>
              <select
                value={selectedService?.id || ''}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="pmt-select"
              >
                <option value="">Sélectionner un service</option>
                {availableServices.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - {formatCurrency ? formatCurrency(service.defaultTotal || service.price || 0) : (service.defaultTotal || service.price || 0)}
                  </option>
                ))}
              </select>
              {availableServices.length === 0 && (
                <p style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.5rem', fontWeight: 500 }}>
                  ⚠️ Tous les services disponibles ont déjà été ajoutés
                </p>
              )}
            </div>
          )}

          {isEditing && selectedService && (
            <div className="pmt-info-box blue" style={{ marginBottom: '1rem' }}>
              <p className="pmt-info-box-label">Service:</p>
              <p className="pmt-info-box-value" style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                {selectedService.name}
              </p>
            </div>
          )}

          {selectedService && (
            <>
              <div className="pmt-form-group">
                <label className="pmt-label">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  className="pmt-input"
                />
              </div>

              <div className="pmt-form-group">
                <label className="pmt-label">Prix Total</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customTotal}
                  onChange={(e) => setCustomTotal(e.target.value)}
                  placeholder="0.00"
                  className="pmt-input"
                />
              </div>

              {customTotal && formatCurrency && (
                <div className="pmt-info-box blue">
                  <p className="pmt-info-box-label">Total du service:</p>
                  <p className="pmt-info-box-amount">
                    {formatCurrency(parseFloat(customTotal))}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Quantité: {quantity}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="pmt-modal-actions">
            <button
              onClick={handleConfirm}
              aria-busy={isSubmitting}
              disabled={isSubmitting || !selectedService || quantity <= 0 || !customTotal || parseFloat(customTotal) <= 0}
              className="pmt-btn pmt-btn-primary"
            >
              {isSubmitting ? 'Veuillez patienter...' : (isEditing ? 'Modifier' : 'Ajouter')}
            </button>
            <button
              onClick={handleClose}
              className="pmt-btn pmt-btn-secondary"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddServiceModal;
